import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress,
  Paper, IconButton, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { BRAND } from '../theme';
import { parseDetailedError } from '../utils/error';
import DiagnosticAlert from './DiagnosticAlert';

const gizLogo = '/giz-logo.png';
const gopaLogo = '/gopa-logo.png';

export default function Login({ onLogin, sessionExpired }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  // Forgot password dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState(null);
  const [clearingCache, setClearingCache] = useState(false);

  const handleForceClearCache = async () => {
    setClearingCache(true);
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((name) => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
          await reg.unregister();
        }
      }
      localStorage.clear();
      sessionStorage.clear();
    } catch (_) {}
    window.location.href = window.location.origin + '/?v=' + Date.now();
  };

  const handleChange = (e) =>
    setCredentials({ ...credentials, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    setErrorInfo(null);
    try {
      const payload = {
        username: (credentials.username || '').trim(),
        password: credentials.password || '',
      };
      const res = await axios.post(API_ENDPOINTS.LOGIN, payload);
      if (res.data.token) {
        onLogin(res.data.token, res.data.user, res.data.session_expires_at);
      } else {
        setErrorInfo({
          title: 'Authentication Incomplete',
          message: 'The server responded, but did not return a valid session authentication token.',
          severity: 'error',
          remediation: 'Please check your backend authentication token view configuration.',
          targetUrl: API_ENDPOINTS.LOGIN,
          method: 'POST',
        });
      }
    } catch (err) {
      const detailed = parseDetailedError(err, {
        endpoint: API_ENDPOINTS.LOGIN,
        method: 'POST',
        username: credentials.username,
      });
      setErrorInfo(detailed);
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async () => {
    if (!resetEmail.trim()) {
      setResetError({
        title: 'Email Required',
        message: 'Please enter your email address.',
        severity: 'warning',
      });
      return;
    }
    setResetLoading(true);
    setResetError(null);
    setResetMsg('');
    try {
      const res = await axios.post(API_ENDPOINTS.PASSWORD_RESET, { email: resetEmail });
      setResetMsg(res.data.message || 'Reset link sent. Check your email.');
    } catch (err) {
      const detailed = parseDetailedError(err, {
        endpoint: API_ENDPOINTS.PASSWORD_RESET,
        method: 'POST',
        email: resetEmail,
      });
      setResetError(detailed);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      background: `linear-gradient(150deg, #353330 0%, ${BRAND.sidebarBg} 35%, #1C1B19 70%, #121110 100%)`,
    }}>
      {/* Left panel — branding only, no logos */}
      <Box sx={{
        flex: 1, display: { xs: 'none', md: 'flex' },
        flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
        px: 8, color: '#fff',
      }}>
        <Typography variant="h3" fontWeight={800} sx={{ mb: 1, lineHeight: 1.15 }}>
          PRUDEV II
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.85, fontWeight: 400, mb: 2 }}>
          MSME Portfolio Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Box sx={{ width: 36, height: 4, bgcolor: BRAND.gopaGold, borderRadius: 2 }} />
          <Box sx={{ width: 24, height: 4, bgcolor: BRAND.gizRed, borderRadius: 2 }} />
        </Box>
        <Typography variant="body1" sx={{ opacity: 0.65, maxWidth: 380, lineHeight: 1.7 }}>
          Supporting the growth of Micro, Small and Medium Enterprises across Uganda through targeted business development services.
        </Typography>
      </Box>

      {/* Right panel — login form */}
      <Box sx={{
        width: { xs: '100%', md: 440 },
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: '#FFFFFF', p: 3,
        borderLeft: `4px solid ${BRAND.gopaGold}`,
      }}>
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {/* Mobile title (shown only on small screens) */}
          <Box sx={{ display: { md: 'none' }, textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" fontWeight={800} color="primary">PRUDEV II</Typography>
            <Typography variant="caption" color="text.secondary">MSME Portfolio System</Typography>
          </Box>

          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #E7E5E4', position: 'relative', overflow: 'hidden' }}>
            {/* Top dual-brand accent stripe */}
            <Box sx={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${BRAND.gopaGold} 0%, ${BRAND.gopaGold} 52%, ${BRAND.gizRed} 52%, ${BRAND.gizRed} 100%)`,
            }} />
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, mt: 0.5 }}>Sign in</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Use your programme credentials to sign in
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth size="small" label="Username, Email or BGE Code" name="username"
                value={credentials.username} onChange={handleChange}
                disabled={loading} autoComplete="username"
                placeholder="e.g. your email, username, or BGE code"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label="Password" name="password"
                type={showPassword ? 'text' : 'password'}
                value={credentials.password} onChange={handleChange}
                disabled={loading}
                sx={{ mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot password */}
              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Typography
                  variant="caption" color="primary"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => { setResetOpen(true); setResetEmail(''); setResetMsg(''); setResetError(null); }}
                >
                  Forgot password?
                </Typography>
              </Box>

              {sessionExpired && (
                <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                  Your session has expired. Please log in again.
                </Alert>
              )}
              {errorInfo && (
                <DiagnosticAlert
                  errorInfo={errorInfo}
                  onRetry={() => handleSubmit()}
                  sx={{ mb: 2 }}
                />
              )}
              <Button
                type="submit" fullWidth variant="contained" size="large"
                disabled={loading}
                sx={{ py: 1.2, bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark } }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
              </Button>

              <Box sx={{ mt: 2.5, pt: 1.5, borderTop: '1px dashed #E7E5E4', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.74rem' }}>
                  Having trouble signing in or seeing old screens?
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleForceClearCache}
                  disabled={clearingCache}
                  sx={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, color: BRAND.primaryMain }}
                >
                  {clearingCache ? 'Refreshing portal…' : '🔄 Tap here to clear cache & refresh portal'}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Logos footer */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mb: 1.5 }}>
              <Box component="img" src={gizLogo} alt="German Cooperation · Implemented by GIZ" sx={{ height: 38, width: 'auto', display: 'block', opacity: 0.9 }} />
              <Box sx={{ width: 1, height: 28, bgcolor: '#E7E5E4' }} />
              <Box component="img" src={gopaLogo} alt="GOPA Pro" sx={{ height: 22, width: 'auto', display: 'block', opacity: 0.9 }} />
            </Box>
            <Typography variant="caption" color="text.disabled">
              © {new Date().getFullYear()} PRUDEV II Programme · GIZ · GOPA Pro
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Forgot password dialog */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the email address linked to your account and we'll send you a reset link.
          </Typography>
          <TextField
            fullWidth size="small" label="Email address" type="email"
            value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
            disabled={resetLoading || !!resetMsg}
            autoFocus
          />
          {resetError && (
            <DiagnosticAlert
              errorInfo={resetError}
              onRetry={handleResetRequest}
              sx={{ mt: 2 }}
            />
          )}
          {resetMsg && <Alert severity="success" sx={{ mt: 2, py: 0.5 }}>{resetMsg}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          {!resetMsg && (
            <Button
              variant="contained" onClick={handleResetRequest}
              disabled={resetLoading}
              sx={{ bgcolor: BRAND.primaryMain, '&:hover': { bgcolor: BRAND.primaryDark } }}
            >
              {resetLoading ? <CircularProgress size={18} color="inherit" /> : 'Send Reset Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
